import { tracked } from '@glimmer/tracking'

const STORE_WRAPPER = Symbol('store-wrapper')

export class StoreWrapper<T> {
  @tracked current: T

  constructor(initial: T) {
    this.current = initial
      ;(this as any)[STORE_WRAPPER] = true
  }
}

export function isStoreWrapper(x: unknown): x is StoreWrapper<any> {
  return Boolean(x && (x as any)[STORE_WRAPPER])
}

/**
 * Creates a stable proxy object that forwards all property access to wrapper.current.
 * Consumers can treat it like the real store instance.
 */
export function createForwardingProxy<T extends object>(wrapper: StoreWrapper<T>): T {
  return new Proxy(Object.create(null), {
    get(_target, prop, _receiver) {
      // allow introspection / debugging
      if (prop === STORE_WRAPPER) return true
      if (prop === 'instance') return wrapper.current
      if (prop === 'constructor') return (wrapper.current as any).constructor

      return Reflect.get(wrapper.current as any, prop)
    },

    set(_target, prop, value) {
      return Reflect.set(wrapper.current as any, prop, value)
    },

    has(_target, prop) {
      return prop in (wrapper.current as any)
    },

    ownKeys() {
      return Reflect.ownKeys(wrapper.current as any)
    },

    getOwnPropertyDescriptor(_target, prop) {
      return Object.getOwnPropertyDescriptor(wrapper.current as any, prop)
    },

    getPrototypeOf() {
      return Object.getPrototypeOf(wrapper.current as any)
    }
  }) as unknown as T
}
